import React, { useMemo, useState, useEffect } from "react";
import toast from "react-hot-toast";

// helpers
const isClosed = (v) => String(v).toLowerCase() === "true";

const getEmployeeDay = (dayObj, employeeId) => {
  const sched = dayObj?.schedule;
  if (!sched || typeof sched !== "object") return null;

  const idStr = String(employeeId ?? "").trim();
  if (!idStr) return null;

  // 1) direct match
  if (sched[idStr]) return sched[idStr];

  // 2) try trimmed key match
  const matchKey = Object.keys(sched).find((k) => String(k).trim() === idStr);
  if (matchKey) return sched[matchKey];

  // 3) fallback: if only one schedule exists, use it
  const keys = Object.keys(sched);
  if (keys.length === 1) return sched[keys[0]];

  return null;
};

function buildTimeOptionsFromDay(dayObj, employeeId) {
  const day = getEmployeeDay(dayObj, employeeId);
  if (!day || isClosed(day.isClosed) || !Array.isArray(day.slots)) return [];

  return day.slots.map((s, i) => ({
    value: `${s.open} - ${s.close}`,
    label: `${s.open} - ${s.close} (Patients: ${s.patients ?? 0})`,
    raw: s,
    key: i,
  }));
}


export function SchedulePickerToast({
  t,
  scheduleList,
  employeeId,
  employee,
  employeeName,
  onConfirm,
}) {
  const availableDates = useMemo(() => {
    // 🔎 debug (remove later)
    console.log("employeeId (selectedOption.value):", employeeId, typeof employeeId);
    console.log("scheduleList[0]?.schedule keys:", Object.keys(scheduleList?.[0]?.schedule || {}));

    return (scheduleList || []).filter((d) => {
      const day = getEmployeeDay(d, employeeId);
      if (!day) return false;
      if (isClosed(day.isClosed)) return false;
      if (!Array.isArray(day.slots) || day.slots.length === 0) return false;
      return true;
    });
  }, [scheduleList, employeeId]);

  const [selectedDateObj, setSelectedDateObj] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");

  // ✅ when availableDates arrive, set default date
  useEffect(() => {
    if (!selectedDateObj && availableDates.length > 0) {
      setSelectedDateObj(availableDates[0]);
    }
  }, [availableDates, selectedDateObj]);

  const timeOptions = useMemo(() => {
    if (!selectedDateObj) return [];
    return buildTimeOptionsFromDay(selectedDateObj, employeeId);
  }, [selectedDateObj, employeeId]);

  // ✅ when timeOptions change, set default time
  useEffect(() => {
    if (!selectedTime && timeOptions.length > 0) {
      setSelectedTime(timeOptions[0].value);
    }
  }, [timeOptions, selectedTime]);

  const [closing, setClosing] = useState(false);

const handleClose = (id) => {
  setClosing(true);
  setTimeout(() => {
    toast.dismiss(id);
  }, 300); // animation duration
};



  return (
<div
  style={{
    width: 340,
    background: "#fff",
    borderRadius: 12,
    padding: 12,
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
    border: "1px solid rgba(0,0,0,0.08)",
 
    transform: closing ? "translateY(20px) scale(0.95)" : "translateY(0) scale(1)",
    opacity: closing ? 0 : 1,
    transition: "all 0.28s cubic-bezier(.21,1.02,.73,1)",
  }}
>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
        Select Date {(employee?.type === 1 && employee?.empType === 3 ) && '& Time'}  {employeeName ? `for ${employeeName}` : ""}
      </div>

      {availableDates.length === 0 ? (
        <div style={{ fontSize: 13, color: "#666" }}>
        <p>  No available schedule found for this employee. </p> 
            <button
              onClick={() => handleClose(t.id)}
              
              className="mt-0 btn btn-danger btn-sm"
            >
              Cancel
            </button>
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gap: 8 }}>
            <select
              value={selectedDateObj?.date || ""}
              onChange={(e) => {
                const d = availableDates.find((x) => x.date === e.target.value);
                setSelectedDateObj(d || null);
                setSelectedTime(""); // reset time so it re-picks default
              }}
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 10,
                border: "1px solid #ddd",
                outline: "none",
              }}
            >
              {availableDates.map((d) => (
                <option key={d.date} value={d.date}>
                  {d.label} ({d.dayName})
                </option>
              ))}
            </select>

{(employee?.type === 1 && employee?.empType === 3 ) && 
 <select
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 10,
                border: "1px solid #ddd",
                outline: "none",
              }}
            >
              {timeOptions.map((opt) => (
                <option key={opt.key} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
 }
           
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              onClick={() => handleClose(t.id)}
              style={{
                flex: 1,
                padding: "10px 10px",
                borderRadius: 10,
                border: "1px solid #ddd",
                background: "#fff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>

            <button
              onClick={() => {
                if (!selectedDateObj?.date || !selectedTime) {
                  toast.error("Please select date and time");
                  return;
                }
                onConfirm({ date: selectedDateObj.date, time: selectedTime });
                handleClose(t.id);
              }}
              style={{
                flex: 1,
                padding: "10px 10px",
                borderRadius: 10,
                border: "none",
                background: "#111",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Confirm
            </button>
          </div>
        </>
      )}
    </div>
  );
}
